import React, { useMemo, useState } from 'react';
import { AutoComplete, Button, Modal, Select, Typography, message } from 'antd';
import { debounce } from 'lodash';
import { useQueryClient } from 'react-query';
import { useGetSuggestUsersAndTeams } from 'client/modules/team/services/hooks/useSuggestUsersAndTeams';
import Divider from '../Divider';
import { useGetListsByUser } from '../../services/hooks/useGetListsByUser';
import { useTransferWatchlist } from '../../hooks/useTransferWatchlist';

const TransferWatchlists = ({ idUser }: { idUser: number }) => {
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [recipientIdUser, setRecipientIdUser] = useState<number | null>(null);
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: listsData, isLoading: isLoadingLists } = useGetListsByUser({
    enabled: true,
    params: { id_user: idUser },
  });

  const {
    data: suggestData,
    refetch: refetchSuggest,
    isFetching: isLoadingSuggest,
  } = useGetSuggestUsersAndTeams({
    enabled: false,
    params: { term: emailSearchTerm },
  });

  const queryClient = useQueryClient();
  const { onSubmit, isLoading: isTransferring } = useTransferWatchlist();

  const loadSuggestions = useMemo(
    () => debounce(() => refetchSuggest(), 500),
    [refetchSuggest]
  );

  const handleEmailSearch = (value: string) => {
    setEmailSearchTerm(value);
    setRecipientIdUser(null);
    if (value.length >= 3) {
      loadSuggestions();
    }
  };

  const handleSelectRecipient = (value: string, option: { idUser: number }) => {
    setEmailSearchTerm(value);
    setRecipientIdUser(option.idUser);
  };

  const userOptions = useMemo(() => {
    if (!suggestData?.results) return [];
    return suggestData.results.flatMap((r) =>
      r.user
        ? [{ value: r.user.email, label: r.user.email, idUser: r.user.id_user }]
        : []
    );
  }, [suggestData]);

  const listOptions = useMemo(() => {
    if (!listsData?.lists) return [];
    return listsData.lists.map((l) => ({ value: l.id_list, label: l.name }));
  }, [listsData]);

  const selectedListName = listOptions.find(
    (o) => o.value === selectedListId
  )?.label;

  const canTransfer = selectedListId !== null && recipientIdUser !== null;

  const handleConfirm = () => {
    if (selectedListId === null || recipientIdUser === null) return;
    onSubmit(selectedListId, recipientIdUser)
      .then(() => {
        message.success(
          `Watchlist "${selectedListName}" transferred to ${emailSearchTerm}`
        );
        queryClient.invalidateQueries(['GetListsByUser', { id_user: idUser }]);
        setIsModalOpen(false);
        setSelectedListId(null);
        setRecipientIdUser(null);
        setEmailSearchTerm('');
      })
      .catch(() => {
        message.error('Transfer failed. Please try again.');
        setIsModalOpen(false);
      });
  };

  return (
    <>
      <Typography.Title level={4} className="!font-medium mt-4">
        Transfer Watchlists
      </Typography.Title>
      <Divider className="border-t-2 border-black border-solid" />
      <p className="mb-4">
        Transfer ownership of this user&apos;s watchlists to another user. The
        original owner will remain shared on the watchlist.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Select
          placeholder="Select watchlist"
          loading={isLoadingLists}
          options={listOptions}
          value={selectedListId}
          onChange={(val) => setSelectedListId(val)}
          className="flex-1"
        />
        <span className="text-base">Transfer ownership to</span>
        <AutoComplete
          value={emailSearchTerm}
          options={userOptions}
          onSearch={handleEmailSearch}
          onSelect={handleSelectRecipient}
          placeholder="Search for a user email"
          notFoundContent={isLoadingSuggest ? 'Searching...' : null}
          className="flex-1"
        />
        <Button
          type="primary"
          disabled={!canTransfer}
          onClick={() => setIsModalOpen(true)}
        >
          Save and Transfer
        </Button>
      </div>

      <Modal
        title="Confirm Ownership Transfer"
        open={isModalOpen}
        onOk={handleConfirm}
        onCancel={() => setIsModalOpen(false)}
        okText="Transfer"
        okButtonProps={{ loading: isTransferring, disabled: isTransferring }}
        cancelButtonProps={{ disabled: isTransferring }}
      >
        <p>
          Are you sure you want to transfer{' '}
          <strong>&quot;{selectedListName}&quot;</strong> to{' '}
          <strong>{emailSearchTerm}</strong>?
        </p>
        <p>This cannot be undone.</p>
      </Modal>
    </>
  );
};

export default TransferWatchlists;
